import { useQueryClient } from '@tanstack/react-query';
import React, { FC, ReactElement, useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';

import { AlertDialogWrapper } from '../../../../components/alert-dialog/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { CloseModalButton } from '../../../../components/close-modal-button/index';
import { Sheet, SheetContent } from '../../../../components/sheet/index';
import { QueryKey } from '../../../../constants/index';
import {
  AnalyticHooks,
  ShareClass as ShareClassHook,
  useModalState,
  useReactForm,
} from '../../../../hooks/index';
import { PossibleAction } from '../../../../types/google-analytics-types.types';
import { DaysPerYearOption, ShareClass } from '../../../../types/share-classes.types';
import { getS3FileOriginalName } from '../../../../utils/get-s3file-original-name.util';
import { handleFormInvalid } from '../../../../utils/handle-form-invalid.util';
import { toNumber } from '../../../../utils/to-number.util';
import { excludeDocusignFileLinks } from '../../../events/event-form/utils/resolve-linked-signature-file-links.util';
import { ChildrenFormProps, EditShareClassStepVariant } from '../modals.types';
import { FormSchema, formSchema } from '../modals.validation';
import { EditPreview, StepFour, StepOne, StepThree, StepTwo } from '../steps/index';
import { UpdateShareClassModal } from '../update-share-class-modal';

type ModalProps = {
  companyId: string;
  data: ShareClass;
  handleClose: () => void;
  invalidateQuery: () => void;
  isOpenModal: boolean;
  onEditClick: (props: { id: string; step: EditShareClassStepVariant }) => void;
  shareClassId: string;
  step: EditShareClassStepVariant;
};

const formSteps: ((
  props: { onEditClick: (step: EditShareClassStepVariant) => void } & ChildrenFormProps,
) => ReactElement)[] = [
  (props) => <StepOne {...props} />,
  (props) => <StepTwo {...props} />,
  (props) => <StepThree {...props} />,
  (props) => <StepFour {...props} />,
  (props) => <EditPreview {...props} />,
];

export const EditShareClassForm: FC<ModalProps> = ({
  companyId,
  isOpenModal,
  handleClose,
  invalidateQuery,
  onEditClick,
  shareClassId,
  step,
  data,
}) => {
  const queryClient = useQueryClient();

  const values: FormSchema = useMemo(() => {
    const participating = data?.liquidityPreferences?.participating
      ? {
          enabled: true,
          capValue: data?.liquidityPreferences?.participating?.capValue,
          interest: data?.liquidityPreferences?.participating?.interest as number,
          daysPersYear: data?.liquidityPreferences?.participating
            ?.daysPersYear as DaysPerYearOption,
        }
      : undefined;

    const liquidityPreferences = data?.liquidityPreferences
      ? {
          enabled: true,
          multiple: data?.liquidityPreferences?.multiple,
          seniority: data?.liquidityPreferences?.seniority,
          participating,
        }
      : undefined;

    const antiDilutionRights = data?.antiDilutionRights
      ? {
          enabled: true,
          base: data?.antiDilutionRights?.base,
        }
      : undefined;

    const conversionRatio = data.conversionRatioEnabled
      ? {
          enabled: data.conversionRatioEnabled,
          value: data.conversionRatio,
        }
      : undefined;

    const votingRight = data.votingRightEnabled
      ? {
          enabled: data.votingRightEnabled,
          value: data.votingRight,
        }
      : undefined;

    // const originalFileName = getS3FileOriginalName(data.docLink);

    // const docData = originalFileName
    //   ? {
    //       progress: 100,
    //       doc: {
    //         name: originalFileName,
    //         size: 1000,
    //         type: 'application/pdf',
    //       },

    //       docLink: data.docLink || '',
    //     }
    //   : undefined;

    const docusignFileLinks = data?.docusignFileLinks ?? [];
    const manualFilesLinks = excludeDocusignFileLinks(data?.filesLinks ?? [], docusignFileLinks);

    const files: {
      abort: () => void;
      doc: {
        name: string;
        size: number;
        type: string;
      };
      docLink: string;
      id: string;
      loadProgress: 100;
    }[] = manualFilesLinks.map((link) => {
      const originalFileName = getS3FileOriginalName(link);

      return {
        abort: () => '',
        loadProgress: 100,
        doc: {
          name: originalFileName || '',
          size: 10,
          type: 'application/pdf',
        },
        docLink: link,
        id: uuidv4(),
      };
    });

    return {
      stepOne: {
        creationDate: new Date(data.creationDate),
        name: data.name,
        dividendRightEnabled: data.dividendRightEnabled,
        conversionRatio,
        votingRight,
      },
      stepTwo: {
        liquidityPreferences,
      },
      stepThree: {
        antiDilutionRights,
      },

      stepFour: {
        additionalNotes: data?.additionalNotes || undefined,
        files: files,
      },
    };
  }, [data]);

  const {
    control,
    reset,
    setValue,
    getValues,
    getFieldState,
    handleSubmit,
    setError,
    clearErrors,
    formState: { errors },
  } = useReactForm({
    schema: formSchema,
    values,
    mode: 'onChange', // Validate on change
    reValidateMode: 'onChange', // Re-validate on change
  });
  const { update } = ShareClassHook.useUpdate();
  const effectiveDocusignFileLinks = data?.docusignFileLinks;

  const companyEditShareClassTracker = AnalyticHooks.useClick(PossibleAction.EDIT_SHARE_CLASS);

  const handleCloseModal = useCallback(() => {
    handleClose();
    queryClient.invalidateQueries({ queryKey: [QueryKey.GET_SHARE_CLASS] });
    reset();
  }, [reset, handleClose, queryClient]);

  const handleUpdateData = useCallback(
    (data: FormSchema) => {
      const { stepOne, stepFour, stepThree, stepTwo } = data;

      const participating = stepTwo?.liquidityPreferences?.participating
        ? stepTwo?.liquidityPreferences?.participating?.daysPersYear
          ? {
              capValue: toNumber(stepTwo?.liquidityPreferences?.participating?.capValue),
              interest: stepTwo?.liquidityPreferences?.participating?.interest,
              daysPersYear: Number(
                stepTwo?.liquidityPreferences?.participating?.daysPersYear,
              ) as unknown as DaysPerYearOption,
            }
          : {
              capValue: toNumber(stepTwo?.liquidityPreferences?.participating?.capValue),
            }
        : null;

      const liquidityPreferences = stepTwo?.liquidityPreferences
        ? {
            multiple: stepTwo?.liquidityPreferences?.multiple,
            seniority: stepTwo?.liquidityPreferences?.seniority,
            participating,
          }
        : null;

      const antiDilutionRights = stepThree?.antiDilutionRights
        ? {
            base: stepThree?.antiDilutionRights?.base,
          }
        : null;

      update(
        {
          companyId,
          data: {
            name: stepOne?.name,
            creationDate: new Date(stepOne.creationDate).toISOString(),
            votingRightEnabled: stepOne?.votingRight?.enabled || false,
            votingRight: stepOne?.votingRight?.value || 0,
            conversionRatioEnabled: stepOne?.conversionRatio?.enabled || false,
            conversionRatio: stepOne?.conversionRatio?.value || 0,
            dividendRightEnabled: stepOne?.dividendRightEnabled,
            liquidityPreferences,
            antiDilutionRights,
            additionalNotes: stepFour?.additionalNotes,
            filesLinks: Array.from(
              new Set(
                [
                  ...(effectiveDocusignFileLinks ?? []),
                  ...(stepFour?.files?.map(({ docLink }) => docLink).filter(Boolean) ?? []),
                ].filter(Boolean),
              ),
            ),
          },
          shareClassId,
        },
        {
          onSuccess: () => {
            companyEditShareClassTracker();
            toast.success(<AppFormattedMessage id="SHARE_CLASS_UPDATED" />);
            handleCloseModal();
            invalidateQuery();
          },
        },
      );
    },
    [
      companyEditShareClassTracker,
      companyId,
      handleCloseModal,
      invalidateQuery,
      effectiveDocusignFileLinks,
      shareClassId,
      update,
    ],
  );

  const {
    toggler: updateModalToggler,
    isOpen: isOpenUpdateModal,
    handleOpenModal: handleOpenUpdateModal,
    handleCloseModal: handleCloseUpdateModal,
    handleSuccessModal: handleSuccessUpdateModal,
  } = useModalState({
    onSuccess: () => {
      handleSubmit(handleUpdateData, handleFormInvalid)();
      handleCloseModal();
    },
  });

  return (
    <Sheet onOpenChange={(open) => !open && handleCloseModal()} open={isOpenModal}>
      <SheetContent
        className="w-full max-w-[485px] border-transparent bg-transparent p-2 shadow-none"
        onInteractOutside={handleCloseModal}
        side="RIGHT"
      >
        <AlertDialogWrapper control={{ onOpenChange: updateModalToggler, open: isOpenUpdateModal }}>
          <UpdateShareClassModal
            onClose={handleCloseUpdateModal}
            onSuccess={handleSuccessUpdateModal}
          />
        </AlertDialogWrapper>
        <div className="flex h-full w-full flex-col overflow-hidden rounded-lg border-[1px] border-gray-300 bg-gray-100 pb-4">
          <div className="flex h-fit w-full items-center justify-between bg-white px-6 py-3">
            <span className="text-xl font-[550] text-gray-700">
              <AppFormattedMessage id="EDIT_SHARE_CLASS" />
            </span>
            <CloseModalButton onClose={handleCloseModal} />
          </div>

          <div className="flex h-full flex-col gap-4 overflow-hidden">
            {formSteps[step - 1]({
              companyId,
              docusignFileLinks: effectiveDocusignFileLinks,
              setError,
              formData: getValues,
              nextFormStep: handleOpenUpdateModal,
              prevFormStep: () => {},
              setFormData: setValue,
              control,
              handleCloseModal,
              filedState: getFieldState,
              errors,
              lockMode: true,
              clearErrors,
              onEditClick: (step) => onEditClick({ id: shareClassId, step }),
            })}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
